/* eslint-disable react-hooks/set-state-in-effect */
/**
 * Admin → Pending Registrations review page.
 *
 * Lists every social actor / investor account in `pending_review` or
 * `pending_documents` state, shows submitted fields + uploaded documents
 * (with per-document download), and lets the admin Approve (which
 * auto-creates a profile in the matching registry) or Reject with reason.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  ArrowLeft, Building2, Briefcase, Download, CheckCircle2, XCircle,
  Clock, Loader2, FileText, Mail, Phone, Globe, Hash, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtDate = (d) => d ? new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const fmtSize = (b = 0) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`;

const PendingRegistrations = () => {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null); // user object
  const [reason, setReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/admin/pending-registrations`, { headers: headers() });
      setPending(res.data.pending || []);
    } catch {
      toast.error('Failed to load pending registrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, []);

  const approve = async (user) => {
    setActionLoading(user.id);
    try {
      await axios.post(`${API}/auth/admin/pending-registrations/${user.id}/approve`, {}, { headers: headers() });
      toast.success(`${user.username} approved`);
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    } finally {
      setActionLoading(null);
    }
  };

  const reject = async () => {
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    setActionLoading(rejecting.id);
    try {
      await axios.post(
        `${API}/auth/admin/pending-registrations/${rejecting.id}/reject`,
        { reason: reason.trim() },
        { headers: headers() },
      );
      toast.success(`${rejecting.username} rejected`);
      setRejecting(null);
      setReason('');
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rejection failed');
    } finally {
      setActionLoading(null);
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const res = await axios.get(
        `${API}/auth/admin/registration-documents/${doc.id}/download`,
        { headers: headers(), responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/users')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to User Management
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pending Registrations</h1>
            <p className="text-sm text-gray-600">Review and approve / reject Social Actor and Investor registrations.</p>
          </div>
        </div>
        <Badge className="bg-amber-100 text-amber-800">
          <Clock className="h-3 w-3 mr-1" /> {pending.length} awaiting review
        </Badge>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
      ) : pending.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-gray-500">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-3" />
            <p>No pending registrations. Everything is up to date.</p>
          </CardContent>
        </Card>
      ) : (
        pending.map((u) => {
          const RoleIcon = u.role === 'social_actor' ? Building2 : Briefcase;
          const payload = u.registration_payload || {};
          const reqs = u.document_requirements || { required: [], optional: [] };
          const uploaded = u.documents || [];
          const uploadedByKey = Object.fromEntries(uploaded.map((d) => [d.document_type, d]));
          const incomplete = reqs.required.some((r) => !uploadedByKey[r.key]);
          const isLoading = actionLoading === u.id;
          return (
            <Card key={u.id} data-testid={`pending-${u.id}`} className="overflow-hidden">
              <CardHeader className="bg-gray-50 pb-3">
                <CardTitle className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <RoleIcon className="h-6 w-6 text-blue-600" />
                    <div>
                      <p className="text-lg font-semibold text-gray-900">{u.organization_name || u.username}</p>
                      <p className="text-xs text-gray-500 font-normal">
                        @{u.username} · {u.role === 'social_actor' ? 'Social Actor' : 'Investor'} ·
                        submitted {fmtDate(u.registration_submitted_at || u.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.registration_status === 'pending_documents' && (
                      <Badge className="bg-amber-100 text-amber-800 text-xs">Awaiting documents</Badge>
                    )}
                    {u.registration_status === 'pending_review' && (
                      <Badge className="bg-blue-100 text-blue-800 text-xs">Ready for review</Badge>
                    )}
                    {incomplete && (
                      <Badge className="bg-red-100 text-red-700 text-xs">Missing required docs</Badge>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>

              <CardContent className="pt-4 space-y-4">
                {/* Submitted details */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-xs text-gray-500 flex items-center gap-1"><Mail className="h-3 w-3" />Email</span><p className="font-medium">{u.email}</p></div>
                  <div><span className="text-xs text-gray-500 flex items-center gap-1"><Phone className="h-3 w-3" />Phone</span><p className="font-medium">{payload.contact_phone || '—'}</p></div>
                  <div><span className="text-xs text-gray-500 flex items-center gap-1"><Hash className="h-3 w-3" />Tax ID</span><p className="font-medium">{payload.tax_id || '—'}</p></div>
                  <div><span className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />Established</span><p className="font-medium">{payload.year_established || '—'}</p></div>
                  <div><span className="text-xs text-gray-500">Legal form</span><p className="font-medium">{payload.legal_form || '—'}</p></div>
                  <div><span className="text-xs text-gray-500">Country</span><p className="font-medium">{payload.country || '—'}</p></div>
                  {payload.website && (
                    <div className="col-span-2"><span className="text-xs text-gray-500 flex items-center gap-1"><Globe className="h-3 w-3" />Website</span><a href={payload.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate block">{payload.website}</a></div>
                  )}
                  {u.role === 'social_actor' && payload.sector && (
                    <div className="col-span-4"><span className="text-xs text-gray-500">Sector</span><p className="font-medium">{payload.sector}</p></div>
                  )}
                  {u.role === 'social_actor' && payload.mission_statement && (
                    <div className="col-span-4"><span className="text-xs text-gray-500">Mission statement</span><p className="text-sm">{payload.mission_statement}</p></div>
                  )}
                  {u.role === 'investor' && payload.investment_focus && (
                    <div className="col-span-4"><span className="text-xs text-gray-500">Investment focus</span><p className="font-medium">{payload.investment_focus}</p></div>
                  )}
                  {u.role === 'investor' && payload.capital_available !== undefined && payload.capital_available !== null && (
                    <div className="col-span-4"><span className="text-xs text-gray-500">Capital available</span><p className="font-medium">€ {Number(payload.capital_available).toLocaleString('de-DE')}</p></div>
                  )}
                </div>

                {/* Documents checklist */}
                <div>
                  <p className="text-sm font-semibold text-gray-800 mb-2">Documents</p>
                  <ul className="space-y-1.5">
                    {[...reqs.required.map((d) => ({ ...d, required: true })), ...reqs.optional.map((d) => ({ ...d, required: false }))].map((d) => {
                      const doc = uploadedByKey[d.key];
                      return (
                        <li key={d.key} className="flex items-center justify-between border rounded-md px-2 py-1.5 bg-white">
                          <div className="flex items-center gap-2 min-w-0">
                            {doc ? <CheckCircle2 className="h-4 w-4 text-green-700 flex-shrink-0" /> : <XCircle className="h-4 w-4 text-gray-300 flex-shrink-0" />}
                            <div className="min-w-0">
                              <p className="text-sm text-gray-900 truncate">
                                {d.label}
                                {d.required && <span className="ml-2 text-red-600 text-xs">*</span>}
                              </p>
                              {doc && <p className="text-xs text-gray-500">{doc.filename} · {fmtSize(doc.size)}</p>}
                            </div>
                          </div>
                          {doc && (
                            <button onClick={() => downloadDocument(doc)} className="p-1 rounded hover:bg-blue-50" title="Download">
                              <Download className="h-3.5 w-3.5 text-blue-600" />
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-end gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    onClick={() => { setRejecting(u); setReason(''); }}
                    disabled={isLoading}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`reject-${u.id}`}
                  >
                    <XCircle className="h-4 w-4 mr-1" /> Reject
                  </Button>
                  <Button
                    onClick={() => approve(u)}
                    disabled={isLoading || incomplete}
                    className="bg-green-700 hover:bg-green-800"
                    data-testid={`approve-${u.id}`}
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                    Approve & create profile
                  </Button>
                </div>
                {incomplete && (
                  <p className="text-xs text-amber-700 text-right">
                    Approval is blocked until all required documents are uploaded.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Rejection dialog */}
      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject {rejecting?.organization_name || rejecting?.username}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              The applicant will receive a notification with the reason below.
            </p>
            <Textarea
              rows={4}
              placeholder="Reason for rejection (required)…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="reject-reason"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
              <Button onClick={reject} disabled={!reason.trim()} className="bg-red-600 hover:bg-red-700" data-testid="reject-confirm">
                <XCircle className="h-4 w-4 mr-1" /> Confirm rejection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingRegistrations;
